// The POST /api/v2/stat-tiles request and answer, as pure functions. The answer is
// turned into the legacy singleStatTileData shape, so the legacy tiles render it unchanged.

// The metrics the stat tiles API accepts. A tile outside it sends no request, as the
// backend would reject it with a 400.
const METRIC_IDENTIFIER = /^(gps|testing|forceplate|nutrition|cognition|wellness|helmet)-c?\d+-(avg|sum|max|best)$/;
const UDM_IDENTIFIER = /^[a-z]+-c\d+-/;
const LEGACY_NO_DATA = "No Data";
const NO_BENCHMARK = "-";
// The legacy endpoint sends this color for every benchmark.
const BENCHMARK_COLOR = "#000000";

// Every id of each position name: the legacy filter matches every position with that name.
// Null when a name has no known id, as a smaller cohort would give wrong numbers.
function positionIdsFor(names, entities) {
  const ids = names.map((name) => entities.filter((p) => p.name === name).map((p) => Number(p.id)));
  return ids.some((list) => list.length === 0) ? null : ids.flat();
}

function cohortSubject(positionNames, playerGroupIds, positionEntities) {
  const positionIds = positionIdsFor(positionNames ?? [], positionEntities ?? []);
  if (positionIds === null) return null;
  const groupIds = (playerGroupIds ?? []).map(Number);
  return positionIds.length || groupIds.length
    ? { kind: "cohort", position_ids: positionIds, player_group_ids: groupIds }
    : { kind: "team" };
}

/**
 * Build the request body for one tile from the props of a compact stat tile. Return null
 * when the stat tiles API cannot express the tile: a metric it does not accept, no dates,
 * a player tile with no player, or a position name with no known id.
 */
export function buildTileRequest({
  identifier, comparisonKey, startDate, endDate, type, playerId, positionNames, playerGroupIds, positionEntities,
}) {
  if (!METRIC_IDENTIFIER.test(identifier ?? "") || !startDate || !endDate) return null;
  const tile = { metric: identifier, comparison_key: comparisonKey, start_date: startDate, end_date: endDate };
  if (type === "player") {
    return playerId ? { ...tile, subject: { kind: "player", player_id: Number(playerId) }, include_benchmarks: true } : null;
  }
  const subject = cohortSubject(positionNames, playerGroupIds, positionEntities);
  return subject ? { ...tile, subject, include_benchmarks: false } : null;
}

/**
 * Call `show` at most once per `windowMs`: when every tile of a page fails at once, the
 * coach sees one message, not one per tile.
 */
export function createThrottledReporter(show, { windowMs = 3000, now = () => Date.now() } = {}) {
  let last = -Infinity;
  return (...args) => {
    const time = now();
    if (time - last < windowMs) return;
    last = time;
    show(...args);
  };
}

// A value block holds a number, or a text label (a wellness answer) in `display` only.
function numberOrText(block) {
  return block ? (block.value ?? block.display) : null;
}

function benchmarkText(benchmark) {
  if (!benchmark) return NO_BENCHMARK;
  if ("left" in benchmark || "right" in benchmark) {
    return `${benchmark.left?.display ?? NO_BENCHMARK} | ${benchmark.right?.display ?? NO_BENCHMARK}`;
  }
  return benchmark.display;
}

function sideFields(side) {
  return {
    latest_value: numberOrText(side?.current),
    compared_to_value: numberOrText(side?.baseline),
    diff: side?.delta?.value ?? null,
  };
}

function noDataLegacy(base, metric, endDate) {
  // The legacy endpoint answers a user-defined metric with no data with zeros dated to the
  // end of the range, not "No Data".
  if (UDM_IDENTIFIER.test(metric.identifier ?? "")) {
    return { ...base, latest_value: 0, compared_to_value: 0, diff: 0, latest_date: endDate };
  }
  return { ...base, latest_value: LEGACY_NO_DATA, compared_to_value: null, diff: null, latest_date: null,
           num_decimals: metric.num_decimals };
}

function sidesLegacy(sides) {
  return sides
    ? { is_asym: true, left: sideFields(sides.left), right: sideFields(sides.right) }
    : { is_asym: false, left: null, right: null };
}

/**
 * Turn a stat tiles API answer into the legacy singleStatTileData shape. Return null for
 * an answer the tile shows as "-" (error or forbidden). `endDate` is the request's end date.
 */
export function toLegacyStatTile(answer, { endDate = null } = {}) {
  const metric = answer?.metric ?? {};
  const base = {
    name: metric.name,
    low_good: metric.low_good,
    is_asym: false,
    left: null,
    right: null,
    team: { value: NO_BENCHMARK, color: BENCHMARK_COLOR },
    pos: { value: NO_BENCHMARK, color: BENCHMARK_COLOR },
  };
  if (answer?.status === "no_data") return noDataLegacy(base, metric, endDate);
  if (answer?.status !== "ok") return null;

  return {
    ...base,
    latest_value: numberOrText(answer.current),
    compared_to_value: numberOrText(answer.baseline),
    diff: answer.delta?.value ?? null,
    latest_date: answer.latest_date ?? null,
    num_decimals: metric.num_decimals,
    ...sidesLegacy(answer.sides),
    team: { value: benchmarkText(answer.benchmarks?.team), color: BENCHMARK_COLOR },
    pos: { value: benchmarkText(answer.benchmarks?.position), color: BENCHMARK_COLOR },
  };
}
